import styled, { css } from 'styled-components';
import { ReferenceDetailsWithPositionPoints } from '../types';
import { ArdoqId } from '@ardoq/api-types';
import { Rectangle } from '@ardoq/graph';

type ReferencesAsLinesProps = {
  references: ReferenceDetailsWithPositionPoints[];
  referenceContextMenuAttr?: string;
  onSetReferenceInContextPath: (referenceId: ArdoqId) => void;
  onReferenceClick: (referenceId: ArdoqId) => void;
  viewBounds: Rectangle;
};

export const ReferencesAsLines = ({
  references,
  onSetReferenceInContextPath,
  referenceContextMenuAttr,
  onReferenceClick,
  viewBounds,
}: ReferencesAsLinesProps) => {
  const referencesAsLines = references.map(reference => (
    <ReferenceAsLine
      key={reference.referenceId}
      {...reference}
      onSetReferenceInContextPath={onSetReferenceInContextPath}
      isCurrentlyDisplayed={reference.isCurrentlyDisplayed ?? true}
      isHighlighted={!!reference.isHighlighted}
      onClick={onReferenceClick}
      referenceContextMenuAttr={referenceContextMenuAttr}
      viewBounds={viewBounds}
    />
  ));

  return <>{referencesAsLines}</>;
};

type ReferenceAsLineProps = {
  startPoint: [number, number];
  startControlPoint: [number, number];
  endControlPoint: [number, number];
  endPoint: [number, number];
  fill: string;
  stroke: string;
  strokeDasharray?: string;
  lineBeginning: string;
  lineEnding: string;
  referenceId: ArdoqId;
  isCurrentlyDisplayed: boolean;
  isHighlighted: boolean;
  onSetReferenceInContextPath: (referenceId: ArdoqId) => void;
  onClick: (referenceId: ArdoqId) => void;
  referenceContextMenuAttr?: string;
  viewBounds: Rectangle;
};

const ReferenceAsLine = ({
  startPoint,
  startControlPoint,
  endControlPoint,
  endPoint,
  fill,
  stroke,
  strokeDasharray,
  lineBeginning,
  lineEnding,
  referenceContextMenuAttr,
  referenceId,
  isCurrentlyDisplayed,
  isHighlighted,
  onClick,
  onSetReferenceInContextPath,
  viewBounds: [, , viewWidth, viewHeight],
}: ReferenceAsLineProps) => {
  const pathInstructions = `M ${startPoint} C ${startControlPoint} ${endControlPoint} ${endPoint}`;

  const handleClick = (e: React.MouseEvent<SVGGElement, MouseEvent>) => {
    e.stopPropagation();
    onClick(referenceId);
  };

  return (
    <LineContainer
      onClick={handleClick}
      onDoubleClick={() => onSetReferenceInContextPath(referenceId)}
      data-testid={isCurrentlyDisplayed ? 'reference' : 'hidden-reference'}
      $isCurrentlyDisplayed={isCurrentlyDisplayed}
      transform={`translate(${viewWidth / 2}, ${viewHeight / 2})`}
      {...(isCurrentlyDisplayed && referenceContextMenuAttr
        ? { [referenceContextMenuAttr]: referenceId }
        : {})}
    >
      <Line
        d={pathInstructions}
        $lineBeginning={lineBeginning}
        $lineEnding={lineEnding}
        style={{ stroke, fill, strokeDasharray }}
      />
      <OnHoverShadow
        d={pathInstructions}
        data-testid={
          isHighlighted ? 'reference-higlight' : 'hidden-reference-higlight'
        }
        $isCurrentlyDisplayed={isCurrentlyDisplayed}
        $isHighlighted={isHighlighted}
      />
    </LineContainer>
  );
};
type IsCurrentlyDisplayed = {
  $isCurrentlyDisplayed: boolean;
};
type IsHighlighted = {
  $isHighlighted: boolean;
};
const LineContainer = styled.g<IsCurrentlyDisplayed>`
  transition: opacity 200ms ease-in-out;

  ${props =>
    props.$isCurrentlyDisplayed
      ? css`
          opacity: 1;
          pointer-events: auto;
          cursor: pointer;
        `
      : css`
          opacity: 0;
          pointer-events: none;
          cursor: grab;
        `};
`;

type LineProps = { $lineBeginning: string; $lineEnding: string };
const Line = styled.path<LineProps>`
  stroke-width: 1px;
  pointer-events: stroke;
  marker-start: ${props => `url(#${props.$lineBeginning})`};
  marker-end: ${props => `url(#${props.$lineEnding})`};
`;

const OnHoverShadow = styled.path<IsCurrentlyDisplayed & IsHighlighted>`
  fill: none;
  stroke-width: 24px;
  pointer-events: stroke;
  stroke-dasharray: none;
  opacity: 0;
  transition: opacity 400ms ease-out;
  cursor: ${props => (props.$isCurrentlyDisplayed ? 'pointer' : 'grab')};

  ${props =>
    props.$isHighlighted &&
    css`
      transition: opacity 250ms ease-out;
      opacity: 0.2;
    `}

  &:hover {
    transition: opacity 250ms ease-out;
    opacity: 0.2;
  }
`;
